import * as React from 'react'

import {
  Box,
  CheckBox,
  Icon,
  Table,
  Text,
  Tooltip,
  useTableDensity,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { selectableColumnWidth } from 'modules/core/components/DataTable/constants'
import { PaginationStateContext } from 'modules/core/components/DataTable/contexts/PaginationStateContext'
import { Selectable } from 'modules/core/components/DataTable/core/types'
import useData from 'modules/core/components/DataTable/hooks/useData'
import useSelectedRows from 'modules/core/components/DataTable/hooks/useSelectedRows'
import useSort from 'modules/core/components/DataTable/hooks/useSort'
import useSticky from 'modules/core/components/DataTable/hooks/useSticky'
import type { HeadCell } from 'modules/core/components/DataTable/types'

interface Props<S extends string> {
  header: HeadCell<S>[]
  selectable?: Selectable
}

function SelectableHeadCell<T, S extends string>({
  sticky,
  selectable,
}: {
  sticky?: boolean
  selectable?: (item: T) => boolean
}) {
  const { selectedRows, onSelectRows, onUnselectRows } = useSelectedRows()
  const { data } = useData<T, S>()
  const tableDensity = useTableDensity()
  const ids = data
    .filter((el) => selectable && selectable(el))
    .map((el) => String((el as { [id: string]: number | string }).id))
    .filter(Boolean) as string[]

  const allRowsSelected =
    data.length > 0 && selectedRows.size > 0 && selectedRows.size === ids.length
  const onClickRowsSelector = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (allRowsSelected) {
      onUnselectRows(ids)
    } else {
      onSelectRows(ids)
    }
  }

  const stickyProp = sticky ? ({ position: 'left' } as const) : undefined

  return (
    <Table.HeadCell width={selectableColumnWidth(tableDensity)} sticky={stickyProp}>
      <CheckBox
        checked={allRowsSelected}
        onClick={onClickRowsSelector}
        disabled={data.length === 0}
      />
    </Table.HeadCell>
  )
}

function TooltipSection({ label }: { label: string }) {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt attachment="bottom center" open={open} toggle={toggle} label={label}>
      <Icon.Medium icon="info-circle" color="grey800" />
    </Tooltip.Alt>
  )
}

function Head<S extends string>({ header, selectable }: Props<S>) {
  const { sortBy, sortOrder, changeSortBy, toggleOrder } = useSort<S>()
  const getStickyProp = useSticky()
  const paginationVariables = React.useContext(PaginationStateContext)

  if (!paginationVariables) throw new Error('PaginationStateContext is not defined')

  const onSort = (sortPath: S) => {
    paginationVariables.resetPagination()
    if (sortBy === sortPath) {
      toggleOrder()
    } else {
      changeSortBy(sortPath)
    }
  }

  const tooltipSection = (label: string) => <TooltipSection label={label} />

  return (
    <Table.Head>
      <Table.Row>
        {selectable && <SelectableHeadCell sticky={header.at(0)?.sticky} selectable={selectable} />}
        {header.map(
          (
            { id, title, width, colSpan, sortPath, sticky, textAlign, justifyContent, tooltip },
            index
          ) => {
            const stickyProp = getStickyProp(index, header.length, !!sticky, !!selectable)

            if (sortPath && sortBy) {
              return (
                <Table.SortableHeadCell
                  key={id}
                  width={width}
                  colSpan={colSpan}
                  justifyContent={justifyContent}
                  onSort={(attribute) => onSort(attribute as S)} // TODO: Type this ok in Table
                  sortAttribute={sortBy}
                  attribute={sortPath}
                  sortDirection={sortOrder}
                  sticky={stickyProp}
                >
                  <Box flexDirection="row" alignItems="center" gap="s4">
                    <Text.Normal ellipsis size="200" weight="semibold">
                      {title}
                    </Text.Normal>
                    {tooltip && tooltipSection(tooltip)}
                  </Box>
                </Table.SortableHeadCell>
              )
            }

            return (
              <Table.HeadCell
                colSpan={colSpan}
                width={width}
                sticky={stickyProp}
                justifyContent={justifyContent}
                key={id}
              >
                {title && (
                  <Box textAlign={textAlign} flexDirection="row" alignItems="center" gap="s4">
                    <Text.Normal ellipsis size="200" weight="semibold">
                      {title}
                    </Text.Normal>
                    {tooltip && tooltipSection(tooltip)}
                  </Box>
                )}
              </Table.HeadCell>
            )
          }
        )}
      </Table.Row>
    </Table.Head>
  )
}

export default Head
